'use client';

import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { cn } from '@utils/utils';
import { SearchType } from '@interfaces/search.types';
import { saveRecentSearch, getRecentSearches } from '@helpers/searchHelpers';
import { SearchComponentProps, SearchState } from '@interfaces/search-component.types';
import { searchBlogs, searchCars } from '@services/searchService';
import SearchInput from '@components/molecules/SearchInput';
import SearchTabs from '@components/molecules/SearchTabs';
import RecentSearches from '@components/molecules/RecentSearches';
import SearchResults from '@components/molecules/SearchResults';
import SearchTrigger from '@components/molecules/SearchTrigger';
import styles from './SearchComponent.module.scss';
import { EventType, trackEventWithCommonProps } from '@utils/eventTracker';
import { useGlobalLoader } from '@hooks/useGlobalLoader';
import { getPageNameFromUrl } from '@utils/pageNameUtils';

export const SearchComponent = ({
  variant = 'desktop',
  initialTab = 'cars',
  onBack,
  isStandalone = false,
  className,
  cityName
}: SearchComponentProps) => {
  const router = useRouter();
  const pathname = usePathname();
  const { showLoader, hideLoader } = useGlobalLoader();
  
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  const [state, setState] = useState<SearchState>({
    searchQuery: '',
    activeTab: initialTab,
    searchResults: null,
    isLoading: false,
    isDropdownOpen: false,
    isFocused: false
  });

  const [pageName, setPageName] = useState<any>('');
  const [recentSearches, setRecentSearches] = useState<any[]>([]);

  useEffect(() => {
    hideLoader();
  }, [pathname, hideLoader]);

  useEffect(() => {
    if(!pathname.includes('search')){
      setPageName(getPageNameFromUrl(pathname));
    }
  }, [pathname]);

  useEffect(() => {
    const loadRecentSearches = () => {
      const allRecent = getRecentSearches();
      setRecentSearches(allRecent);
    };

    loadRecentSearches();
  }, []);

  useEffect(() => {
    const performSearch = async () => {
      const query = state.searchQuery.trim();
      
      if (!state.searchResults || 
          (state.activeTab === 'cars' && !state.searchResults.carsData?.length) ||
          (state.activeTab === 'blogs' && !state.searchResults.blogsData?.length)) {
        setState(prev => ({ ...prev, isLoading: true }));
      }

      try {
        if (query) {
          const [carResponse, blogResponse] = await Promise.all([
            searchCars(query, cityName),
            searchBlogs(query)
          ]);

          setState(prev => ({ 
            ...prev, 
            searchResults: { carsData: carResponse, blogsData: blogResponse }, 
            isLoading: false 
          }));
        } else {
            if (state.isLoading) return;

            if (state.activeTab === 'cars') {
            const carResponse = await searchCars('', cityName);
            setState(prev => ({ 
              ...prev, 
              searchResults: { carsData: carResponse, blogsData: prev.searchResults?.blogsData || [] }, 
              isLoading: false 
            }));
          } else {
            const blogResponse = await searchBlogs('');
            setState(prev => ({ 
              ...prev, 
              searchResults: { carsData: prev.searchResults?.carsData || [], blogsData: blogResponse }, 
              isLoading: false 
            }));
          }
        }
      } catch (error) {
        console.error('Error fetching search results:', error);
        setState(prev => ({ 
          ...prev, 
          searchResults: { carsData: [], blogsData: [] }, 
          isLoading: false 
        }));
      }
    };

    const debounceTimer = setTimeout(() => {
      performSearch();
    }, state.searchQuery.trim() ? 300 : 0);

    return () => clearTimeout(debounceTimer);
  }, [state.searchQuery, state.activeTab, cityName]);

  useEffect(() => {
    if (variant === 'mobile' && inputRef.current) {
      const timer = setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          inputRef.current.setSelectionRange(
            inputRef.current.value.length, 
            inputRef.current.value.length
          );
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [variant]);

  useEffect(() => {
    if (variant === 'desktop') {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target as Node) &&
          !inputRef.current?.contains(event.target as Node)
        ) {
          setState(prev => ({ ...prev, isDropdownOpen: false, isFocused: false }));
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [variant]);

  const handleSearch = async (query: string) => {
    setState(prev => ({ ...prev, searchQuery: query }));
    if (variant === 'desktop') {
      setState(prev => ({ ...prev, isDropdownOpen: true }));
    }
  };

  const handleTabChange = (tab: SearchType) => {
    setState(prev => ({ ...prev, activeTab: tab }));
  };

  const handleClear = async () => {
    await trackEventWithCommonProps('action', EventType.TRACK_EVENT, {
      pageType: pageName,
      additionalProps: {
        action_type: 'tap_btn',
        action_name: 'tap_btn_search_close',
        city_name: cityName,
        search_query: state.searchQuery
      }
    });
    setState(prev => ({ ...prev, searchQuery: '' }));
    inputRef.current?.focus();
  };

  const handleBack = async () => {
    await trackEventWithCommonProps('action', EventType.TRACK_EVENT, {
      pageType: pageName,
      additionalProps: {
        action_type: 'tap_btn',
        action_name: 'tap_btn_search_back',
        city_name: cityName,
        search_query: state.searchQuery
      }
    });

    if (onBack) {
      onBack();
    } else {
      router.back();
    }
  };

  const handleItemClick = async (item: any) => {
    showLoader(`Loading...`);
    const isBrandCollection = (item: any) => {
      return item.name?.toLowerCase().includes('all ') || 
             item.name?.toLowerCase().includes(' cars') ||
             !item.model_name;
    };

    let url: string;
    
    if (state.activeTab === 'cars') {
      url = isBrandCollection(item) 
        ? `/collection/${item.slug}+cars`
        : `/cars/${item.slug}`;
    } else {
      url = `${process.env.NEXT_PUBLIC_FRONTEND_HOST}${item.slug}`;
    }
    
    if (state.searchQuery.trim()) {
      saveRecentSearch(state.searchQuery.trim(), state.activeTab);
      const allRecent = getRecentSearches();
      setRecentSearches(allRecent);
    }

    if (state.activeTab === 'cars') {
      await trackEventWithCommonProps('action', EventType.TRACK_EVENT, {
        pageType: pageName,
        additionalProps: {
          action_type: 'tap_btn',
          action_name: 'tap_btn_search_variant_details',
          city_name: cityName,
          search_navigation: url,
          search_query: state.searchQuery,
          vehicle_make: item?.make_name || '',
          vehicle_model: item?.model_name || ''
        }
      });
    } else {
      await trackEventWithCommonProps('action', EventType.TRACK_EVENT, {
        pageType: pageName,
        additionalProps: {
          action_type: 'tap_btn',
          action_name: 'tap_btn_search_blogs',
          city_name: cityName,
          search_navigation: url,
          search_query: state.searchQuery
        }
      });
    }
    
    router.push(url);
    
    if (variant === 'desktop') {
      setState(prev => ({ ...prev, isDropdownOpen: false }));
    }

    setState(prev => ({ 
      ...prev, 
      isFocused: false 
    }));
  };

  const handleRecentSearchClick = (searchTerm: string, searchType: SearchType) => {
    setState(prev => ({ 
      ...prev, 
      searchQuery: searchTerm,
      activeTab: searchType 
    }));
    if (inputRef.current) {
      inputRef.current.value = searchTerm;
    }
  };

  const handleInputFocus = async () => {
    setState(prev => ({ 
      ...prev, 
      isDropdownOpen: variant === 'desktop',
      isFocused: true 
    }));
    if(variant === 'desktop'){
      await trackEventWithCommonProps('action', EventType.TRACK_EVENT, {
        pageType: getPageNameFromUrl(pathname),
        additionalProps: {
          action_type: 'tap_btn',
          action_name: 'tap_btn_search',
          city_name: cityName,
        }
      });
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && state.searchQuery.trim()) {
      saveRecentSearch(state.searchQuery.trim(), state.activeTab);
      const allRecent = getRecentSearches();
      setRecentSearches(allRecent);
    }
  };

  if (variant === 'mobile' && !isStandalone) {
    return (
      <SearchTrigger 
        onClick={() => router.push('/search')}
        className={className}
      />
    );
  }

  if (variant === 'mobile') {
    return (
      <div className={cn(styles.searchContainer, styles.mobile, className)}>
        <div className={styles.searchHeader}>
          <div className={styles.searchBar}>
            <SearchInput
              variant={variant}
              searchQuery={state.searchQuery}
              onSearch={handleSearch}
              onClear={handleClear}
              onBack={handleBack}
              onFocus={handleInputFocus}
              onKeyPress={handleKeyPress}
              isFocused={state.isFocused}
              inputRef={inputRef}
            />
          </div>
        </div>
        <RecentSearches
          recentSearches={recentSearches}
          onRecentSearchClick={handleRecentSearchClick}
          searchQuery={state.searchQuery}
          variant={variant}
        />
        <SearchTabs
          activeTab={state.activeTab}
          onTabChange={handleTabChange}
          variant={variant}
        />
        <div className={styles.searchResults}>
          <SearchResults
            searchResults={state.searchResults}
            searchQuery={state.searchQuery}
            activeTab={state.activeTab}
            onItemClick={handleItemClick}
            variant={variant}
            isLoading={state.isLoading}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={cn(styles.searchContainer, styles.desktop, className)}>
      <div className={cn(
        styles.searchBar,
        state.isFocused && styles.searchBarExpanded
      )}>
        <SearchInput
          variant={variant}
          searchQuery={state.searchQuery}
          onSearch={handleSearch}
          onClear={handleClear}
          onFocus={handleInputFocus}
          onKeyPress={handleKeyPress}
          isFocused={state.isFocused}
          inputRef={inputRef}
        />
      </div>

      {state.isDropdownOpen && (
        <div ref={dropdownRef} className={styles.searchDropdown}>
          <RecentSearches
            recentSearches={recentSearches}
            onRecentSearchClick={handleRecentSearchClick}
            searchQuery={state.searchQuery}
            variant={variant}
          />
          
          <SearchTabs
            activeTab={state.activeTab}
            onTabChange={handleTabChange}
            variant={variant}
          />
          
          <div className={styles.searchResults}>
            <SearchResults
              searchResults={state.searchResults}
              searchQuery={state.searchQuery}
              activeTab={state.activeTab}
              onItemClick={handleItemClick}
              variant={variant}
              isLoading={state.isLoading}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchComponent; 